/* =========================================================
   ADVANCED AIRFLOW CALCULATOR (Starter -> Working)
   - Reads table rows (room type, area, height)
   - Calculates nominal + boost distribution (reverse-engineered)
   - Renders totals + per-room breakdown
========================================================= */

document.addEventListener("DOMContentLoaded", () => {
  const root = document.getElementById("advancedAirflow");
  if (!root) return;

  const regionEl = root.querySelector("#aacRegion");
  const unitsEl = root.querySelector("#aacUnits");
  const addBtn = root.querySelector("#aacAddRow");
  const recalcBtn = root.querySelector("#aacRecalc");
  const roomsBody = root.querySelector("#aacRoomsBody");

  const statusEl = root.querySelector("#aacStatus");
  const nominalEl = root.querySelector("#aacNominal");
  const boostEl = root.querySelector("#aacBoost");
  const resultsBody = root.querySelector("#aacResultsBody");

  const supplySet = new Set(["Living", "Study", "Master Bedrm", "Double Bedrm", "Single Bedrm"]);
  const extractSet = new Set(["Kitchen", "Bathroom", "Utility", "WC", "Store", "Store >4m2"]);

  function classifyRoom(roomType) {
    if (supplySet.has(roomType)) return "S";
    if (extractSet.has(roomType)) return "E";
    return "—";
  }

  function fmt(n, units) {
    if (!isFinite(n)) return "—";
    const v = Math.round(n * 10) / 10;
    return units === "ls" ? `${v} l/s` : `${v} m³/h`;
  }

  function getBoostRates(units) {
    const isLS = units === "ls";
    return {
      "Kitchen": isLS ? 13 : 47,
      "Bathroom": isLS ? 8 : 29,
      "Utility": isLS ? 8 : 29,
      "WC": isLS ? 6 : 22,
      "Store": isLS ? 3 : 11,
      "Store >4m2": isLS ? 3 : 11
    };
  }

  function wholeDwellingByCount({ region, units, rooms }) {
    const isScotland = region === "Scotland";
    const isLS = units === "ls";

    const bedrooms = rooms.filter(r =>
      ["Master Bedrm", "Double Bedrm", "Single Bedrm"].includes(r.type)
    ).length;

    const hab = rooms.filter(r => ["Living", "Study"].includes(r.type)).length;

    // Spreadsheet logic: Scotland uses (count-1), others use count
    const count = isScotland ? (bedrooms + hab) : bedrooms;

    if (isLS) return isScotland ? (13 + Math.max(count - 1, 0) * 6) : (13 + count * 6);
    return isScotland ? (47 + Math.max(count - 1, 0) * 21.6) : (47 + count * 21.6);
  }

  function wholeDwellingByArea({ units, totalArea }) {
    // Spreadsheet factors
    if (units === "ls") return totalArea * 0.3;
    return totalArea * 1.08;
  }

  function computeVentilation({ region, units, rooms }) {
    const boostRates = getBoostRates(units);

    const enriched = rooms
      .filter(r => r && r.type && Number(r.area) > 0 && Number(r.height) > 0)
      .map(r => {
        const area = Number(r.area);
        const height = Number(r.height);
        const volume = area * height;
        const cls = classifyRoom(r.type);
        return { ...r, area, height, volume, cls };
      });

    const totalArea = enriched.reduce((s, r) => s + r.area, 0);

    // Whole dwelling nominal (max of count method and area method)
    const n6 = wholeDwellingByCount({ region, units, rooms: enriched });
    const n7 = wholeDwellingByArea({ units, totalArea });
    const U7 = Math.max(n6, n7);

    // Wet-room sum (N10)
    const count = (t) => enriched.filter(r => r.type === t).length;

    const N10 =
      count("Kitchen") * (boostRates["Kitchen"] || 0) +
      (count("Bathroom") + count("Utility")) * (boostRates["Bathroom"] || 0) +
      count("WC") * (boostRates["WC"] || 0) +
      count("Store >4m2") * (boostRates["Store >4m2"] || 0);

    // Boost target (N11)
    const N11 = Math.max(N10, U7);

    // Total supply volume
    const totalSupplyVol = enriched
      .filter(r => r.cls === "S")
      .reduce((s, r) => s + r.volume, 0);

    // 1) Supply nominal (J)
    enriched.forEach(r => {
      r.nominal = 0;
      r.boost = 0;

      if (r.cls === "S" && totalSupplyVol > 0) {
        r.nomSupply = U7 * (r.volume / totalSupplyVol);
      } else {
        r.nomSupply = 0;
      }
    });

    const J_total = enriched.reduce((s, r) => s + (r.nomSupply || 0), 0);

    // 2) Extract boost (N) scaled to hit N11
    enriched.forEach(r => {
      if (r.cls !== "E") {
        r.boostExtract = 0;
        return;
      }
      const base = boostRates[r.type] || 0;
      r.boostExtract = (N10 > 0) ? (base * (N11 / N10)) : 0;
    });

    const N_total = enriched.reduce((s, r) => s + (r.boostExtract || 0), 0);

    // 3) Extract nominal (K): cap to match supply nominal if needed
    enriched.forEach(r => {
      if (r.cls !== "E") {
        r.nomExtract = 0;
        return;
      }
      r.nomExtract = (N_total > J_total && N_total > 0)
        ? (r.boostExtract * (J_total / N_total))
        : r.boostExtract;
    });

    const K_total = enriched.reduce((s, r) => s + (r.nomExtract || 0), 0);

    // 4) Supply boost (M): scale to match extract boost
    enriched.forEach(r => {
      if (r.cls !== "S") {
        r.boostSupply = 0;
        return;
      }
      r.boostSupply = (K_total > 0)
        ? (r.nomSupply * (N_total / K_total))
        : 0;
    });

    // Set per-room “nominal/boost” values (depending on S/E)
    enriched.forEach(r => {
      if (r.cls === "S") {
        r.nominal = r.nomSupply || 0;
        r.boost = r.boostSupply || 0;
      } else if (r.cls === "E") {
        r.nominal = r.nomExtract || 0;
        r.boost = r.boostExtract || 0;
      } else {
        r.nominal = 0;
        r.boost = 0;
      }
    });

    return {
      totals: { totalArea, n6, n7, nominalWhole: U7, wetSum: N10, boostTarget: N11 },
      rooms: enriched
    };
  }

  function readRoomsFromUI() {
    const rows = Array.from(roomsBody.querySelectorAll(".aacRow"));
    return rows.map(row => ({
      type: row.querySelector(".aacRoomType")?.value?.trim() || "",
      area: row.querySelector(".aacArea")?.value || "",
      height: row.querySelector(".aacHeight")?.value || "",
      _row: row
    }));
  }

  function renderSELabels() {
    roomsBody.querySelectorAll(".aacRow").forEach(row => {
      const type = row.querySelector(".aacRoomType")?.value || "";
      const seCell = row.querySelector(".aacSE");
      if (seCell) seCell.textContent = classifyRoom(type);
    });
  }

  function renderResults(out) {
    const units = unitsEl.value;

    nominalEl.textContent = fmt(out.totals.nominalWhole, units);
    boostEl.textContent = fmt(out.totals.boostTarget, units);

    // Room breakdown
    resultsBody.innerHTML = "";
    if (!out.rooms.length) {
      resultsBody.innerHTML = `<tr><td colspan="4" class="advancedAirflow__empty">Add rooms with area + height</td></tr>`;
      return;
    }

    out.rooms.forEach(r => {
      const tr = document.createElement("tr");
      tr.innerHTML = `
        <td>${escapeHtml(r.type)}</td>
        <td>${r.cls || "—"}</td>
        <td class="tR">${fmt(r.nominal, units)}</td>
        <td class="tR">${fmt(r.boost, units)}</td>
      `;
      resultsBody.appendChild(tr);
    });
  }

  function escapeHtml(str) {
    return String(str)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function recalc() {
    renderSELabels();

    const region = regionEl.value;
    const units = unitsEl.value;
    const rooms = readRoomsFromUI();

    statusEl.textContent = "Calculating…";

    const out = computeVentilation({ region, units, rooms });
    renderResults(out);

    statusEl.textContent = "Updated";
  }

  function bindRow(row) {
    const typeSel = row.querySelector(".aacRoomType");
    const areaEl = row.querySelector(".aacArea");
    const heightEl = row.querySelector(".aacHeight");
    const removeBtn = row.querySelector(".aacRemove");

    const onEdit = () => {
      statusEl.textContent = "Edited";
      renderSELabels();
      // live calc
      recalc();
    };

    typeSel?.addEventListener("change", onEdit);
    areaEl?.addEventListener("input", onEdit);
    heightEl?.addEventListener("input", onEdit);

    removeBtn?.addEventListener("click", () => {
      row.remove();
      recalc();
    });
  }

function rowHtml() {
  return `
    <tr class="aacRow">
      <td>
        <select class="advancedAirflow__select aacRoomType">
          <option>Living</option>
          <option>Kitchen</option>
          <option>Bathroom</option>
          <option>Utility</option>
          <option>WC</option>
          <option>Study</option>
          <option>Master Bedrm</option>
          <option>Double Bedrm</option>
          <option>Single Bedrm</option>
          <option>Store</option>
          <option>Store >4m2</option>
        </select>
      </td>
      <td><input type="number" class="advancedAirflow__input aacArea" step="0.1" min="0" placeholder="e.g. 10"></td>
      <td><input type="number" class="advancedAirflow__input aacHeight" step="0.1" min="0" placeholder="e.g. 2.4"></td>
      <td class="aacSE">—</td>
      <td><button type="button" class="advancedAirflow__iconBtn aacRemove" aria-label="Remove row">✕</button></td>
    </tr>
  `;
}

function addRow() {
  const row = document.createElement("tbody"); // temp container
  row.innerHTML = rowHtml().trim();
  const tr = row.firstElementChild;
  roomsBody.appendChild(tr);
  bindRow(tr);
  recalc();
}


  // Bind existing
  roomsBody.querySelectorAll(".aacRow").forEach(bindRow);

  // Controls
  addBtn?.addEventListener("click", addRow);
  recalcBtn?.addEventListener("click", recalc);
  regionEl?.addEventListener("change", recalc);
  unitsEl?.addEventListener("change", recalc);

  // Initial calc
  recalc();
});
